import _isEmpty from "lodash/isEmpty";
import _cloneDeep from "lodash/cloneDeep";
import _sortBy from "lodash/sortBy";
import _uniqBy from "lodash/uniqBy";

const state = {
    accessories: {
        streamingBundle: []
    },
    builds: [],
    buildsIndex: null,
    cases: {
        variants: []
    },
    motherboards: {
        variants: []
    },
    cart: {},
    externalStorage: [],
    externalStorageEmpty: {
        id: 0,
        title: "- Select option -"
    },
    games: [],
    selectedMonitor: {
        "starter-pc": null,
        "starter-pc-plus": null,
        "streamer-pc": null
    },
    selectedStorage: null,
    pageData: {},
    resolution: 1080,
    rules: [],
    total: 0,
    which: "",
    route: "",
    isUpdating: false,
    addEssentialStreamingBundle: false,
    isLoadingReviews: false,
    reviews: {
        data: []
    },
    page: 1,
    trackTypes: {
        "starter-pc": "SP",
        "starter-pc-plus": "SP Plus",
        "streaming-pc": "Streaming PC",
        "creator-pc": "CP",
        "h1-mini-pc": "H1",
    },
    upsellMonitor: null
};

const mutations = {
    CLEAN_REVIEWS(state) {
        state.reviews = {
            data: [],
            total_items: 0
        };
    },
    IS_LOADING_REVIEWS(state, isLoading) {
        state.isLoadingReviews = isLoading;
    },
    SET_ACCESSORIES(state, payload) {
        state.accessories = payload;
    },
    SET_BUILDS(state, payload) {
        state.builds = payload;
    },
    SET_BUILDS_INDEX(state, payload) {
        localStorage.setItem(`${state.which}-index`, payload);
        state.buildsIndex = payload;
    },
    SET_CASES(state, payload) {
        state.cases = payload;
    },
    SET_MOTHERBOARDS(state, payload) {
        state.motherboards = payload;
    },
    SET_CART(state, payload) {
        state.cart = payload;
    },
    SET_EXTERNAL_STORAGE(state, payload) {
        state.externalStorage = payload;
    },
    SET_GAMES(state, games) {
        state.games = games;
    },
    SET_PAGE_DATA(state, pageData) {
        state.pageData = pageData;
    },
    SET_PAGE(state, page) {
        state.page = page;
    },
    SET_RESOLUTION(state, resolution) {
        state.resolution = resolution;
    },
    SET_REVIEWS(state, reviews) {
        reviews.data = _sortBy(reviews.data, review => {
            return -moment(review.date).unix()
        });

        state.reviews = reviews;
    },
    SET_ROUTE(state, payload) {
        state.route = payload;
    },
    SET_RULES(state, payload) {
        state.rules = payload;
    },
    SET_SELECTED_MONITOR(state, payload) {
        state.selectedMonitor[state.which] = payload;
    },
    SET_SELECTED_STORAGE(state, payload) {
        state.selectedStorage = payload;
    },
    SET_TOTAL(state, payload) {
        state.total = payload;
    },
    SET_WHICH(state, payload) {
        state.which = payload;
    },
    SET_IS_UPDATING(state, payload) {
        state.isUpdating = payload;
    },
    SET_SHOULD_ADD_ESSENTIAL_STREAMING_BUNDLE(state) {
        state.addEssentialStreamingBundle = !state.addEssentialStreamingBundle;
    },
    SET_UPSELL_MONITOR(state, payload) {
        state.upsellMonitor = payload;
    }
};

const actions = {
    checkForSelectedStorage({ commit }) {
        const selectedStorage = state.externalStorage.filter((storage, index) => {
            return state.cart.hdd.some(hdd => hdd.id == storage.id);
        });

        commit("SET_SELECTED_STORAGE", selectedStorage && selectedStorage[0] || state.externalStorageEmpty);
    },
    cleanReviews({ commit }) {
        commit("CLEAN_REVIEWS");
    },
    getAccessories({ commit, dispatch }) {
        axios({
            url: `/${state.which}/accessories`
        }).then((response) => {
            const accessories = {
                streamingBundle: response.data.streamingBundle || [],
                extras: response.data.extras,
                peripherals: response.data.peripherals,
                monitors: response.data.monitors,
                specials: response.data.specials
            };

            commit("SET_ACCESSORIES", accessories);
            commit("SET_CASES", response.data.cases[0]);
            commit("SET_MOTHERBOARDS", response.data.motherboards && response.data.motherboards[0].variants || []);
            dispatch("processMonitor", response.data.monitors || []);
            dispatch("processStorage", response.data.storage || []);
        });
    },
    getCart({ dispatch }) {
        axios({
            url: `/${state.which}/cart`,
            headers: {
                token: window.CONFIG.SESSION
            }
        }).then((response) => {
            dispatch("setCart", response.data);
            dispatch("getAccessories");
        }).catch((error) => {
            console.log(error);
            dispatch("initCart");
        });
    },
    getData({ commit, dispatch }, group) {
        if (_isEmpty(state.cart) || state.which != group.which) {
            commit("SET_WHICH", group.which);
            commit("SET_ROUTE", group.route);

            axios(`/${group.which}/builds`).then(({ data: starterPc }) => {
                dispatch("setData", starterPc);
                if (localStorage.getItem("init-start-cart")) {
                    localStorage.removeItem("init-start-cart")
                    dispatch("initCart");
                    dispatch("Application/setShowLoading", true, { root: true });
                } else {
                    dispatch("getCart");
                }
            }).catch(error => {
                console.log(error)
            });

            // dispatch("getAccessories");
        }
    },
    getReviews({ commit, dispatch }, shouldCleanOldReviews = false) {
        commit("IS_LOADING_REVIEWS", true);
        axios({
            url: `/promo-build-reviews/${state.builds[state.buildsIndex].id}`,
            params: {
                page: state.page,
                sort: "created_at|desc",
                limit: 10
            }
        }).then(({ data: reviews }) => {
            if (shouldCleanOldReviews) {
                dispatch("cleanReviews");
            }
            const localReview = _cloneDeep(state.reviews.data);
            localReview.push(...reviews.data);
            reviews.data = _uniqBy(localReview, review => review.id);
            commit("SET_REVIEWS", reviews);
            commit("IS_LOADING_REVIEWS", false);
        }).catch(error => {
            console.log(error);
        });
    },
    initCart({ dispatch }) {
        dispatch("isUpdating", true);

        axios({
            method: "POST",
            url: `/${state.which}/builds/${state.builds[state.buildsIndex].id}/init-cart`,
            headers: {
                token: window.CONFIG.SESSION
            }
        }).then(() => {
            dispatch("getCart");
        }).catch((error) => {
            console.log(error);
        }).finally(() => {
            dispatch("isUpdating", false);
            dispatch("Application/setShowLoading", false, { root: true });
        });
    },
    initReviews({ dispatch }) {
        dispatch("setPage", 1);
        dispatch("cleanReviews");
        dispatch("getReviews");
    },
    isUpdating({ commit }, data) {
        commit("SET_IS_UPDATING", data);
    },
    async processMonitor({ commit }, list) {
        await commit("SET_UPSELL_MONITOR", list.find(monitor => monitor.is_upsell == 1) || null);

        if (state.upsellMonitor && state.cart.monitors.find(item => item.products_id == state.upsellMonitor.id)) {
            await commit("SET_SELECTED_MONITOR", state.upsellMonitor);
        }
    },
    async processStorage({ commit, dispatch }, list) {
        const storage = [];

        list.forEach(item => {
            item.variants.forEach(variant => {
                const hdd = _cloneDeep(item);
                hdd.id = variant.id;
                hdd.title = variant.title;
                hdd.variants = [variant];

                storage.push(hdd);
            });
        });

        await commit("SET_EXTERNAL_STORAGE", storage);
        dispatch("checkForSelectedStorage");
    },
    removePart({ commit, dispatch }, part) {
        dispatch("isUpdating", true);

        return axios({
            method: "POST",
            url: `/${state.which}/delete-part/${part.products_id}/part/${part.id}`,
            headers: {
                token: window.CONFIG.SESSION
            }
        }).then(({ data }) => {
            commit("SET_CART", data.build);
            commit("SET_TOTAL", data.total);
        }).finally(() => {
            dispatch("isUpdating", false);
        });
    },
    resetCart({ commit }) {
        commit("SET_CART", {});
        commit("SET_TOTAL", 0);
    },
    setCart({ commit }, data) {
        commit("SET_CART", data.build);
        commit("SET_GAMES", data.games);
        commit("SET_RESOLUTION", data.resolution);
        commit("SET_TOTAL", data.total);
    },
    setData({ commit, dispatch }, data) {
        const buildsIndex = +localStorage.getItem(`${state.which}-index`) || 0;
        commit("SET_BUILDS_INDEX", buildsIndex);
        commit("SET_BUILDS", data.builds);
        commit("SET_PAGE_DATA", data.pageData);
        dispatch("initReviews");
    },
    setResolution({ commit }, data) {
        commit("SET_RESOLUTION", data);

        const formData = new FormData();
        formData.append("session", window.CONFIG.SESSION);
        formData.append("resolution", data);

        axios({
            url: "/set-resolution",
            method: "POST",
            data: formData
        });
    },
    setPage({ commit }, page) {
        commit("SET_PAGE", page);
    },
    setRoute({ commit }, data) {
        commit("SET_ROUTE", data);
    },
    setRules({ commit }, data) {
        commit("SET_RULES", data);
    },
    setSelectedMonitor({ commit }, data) {
        commit("SET_SELECTED_MONITOR", data);
    },
    setSelectedStorage({ commit }, data) {
        commit("SET_SELECTED_STORAGE", data);
    },
    setWhich({ commit }, data) {
        commit("SET_WHICH", data);
    },
    updateModel({ commit, dispatch }, buildsIndex) {
        commit("SET_BUILDS_INDEX", buildsIndex);
        dispatch("initCart");
    },
    updatePart({ commit, dispatch }, { part, qty }) {
        dispatch("isUpdating", true);

        return axios({
            method: "POST",
            url: `/${state.which}/update-part/${part.products_id}/part/${part.id}/qty/${qty}`,
            headers: {
                token: window.CONFIG.SESSION
            }
        }).then(({ data }) => {
            commit("SET_CART", data.build);
            commit("SET_TOTAL", data.total);

            if (data.rules.length) {
                commit("SET_RULES", data.rules);
            }
        }).finally(() => {
            dispatch("isUpdating", false);
        });
    },
    toggleEssentialStreamingBundle({ commit }) {
        commit("SET_SHOULD_ADD_ESSENTIAL_STREAMING_BUNDLE");
    }
};

const getters = {
    pcGallery() {
        const route = state.route == "streaming-pc" ? "streamer-pc" : state.route;
        const gallery = "images_" + route.replace(/-/g, "_") + "_gallery";
        const caseId = state.cart.case && state.cart.case.id;
        const cases = state.cases.variants.find(buildCase => buildCase.id == caseId);
        return cases && cases[gallery] && cases[gallery].map(img => img.image_url) || [];
    },
    getExternalStorage() {
        const hasExternalStorage = (state.externalStorage.length > 0);

        if(hasExternalStorage) {
            return [state.externalStorageEmpty, ...state.externalStorage];
        }

        return null;
    },
    getTrackType() {
        return state.trackTypes[state.route];
    },
    totalBuilds() {
        return state.builds.length;
    },
    totalMonitors() {
        let total = 0;

        state.cart.monitors && state.cart.monitors.forEach(monitor => {
            total += Number.parseInt(monitor.quantity);
        });

        return total;
    },
    reviewCount() {
        return state.reviews.total_items;
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
